import {
  type BrowserPrintStatic,
  ZEBRA_BROWSER_PRINT_UNAVAILABLE_MESSAGE,
  type ZebraPrinterDevice,
} from "./types";

function getBrowserPrint(): BrowserPrintStatic | undefined {
  return (globalThis as unknown as { BrowserPrint?: BrowserPrintStatic }).BrowserPrint;
}

export function isBrowserPrintAvailable(): boolean {
  return typeof getBrowserPrint()?.getDefaultDevice === "function";
}

export function buildPrinterStatusSummary(response: string): string {
  const printerStatus: string[] = [];
  let ok = false;
  const isError = response.charAt(70);
  const media = response.charAt(88);
  const head = response.charAt(87);
  const pause = response.charAt(84);

  if (isError === "0") {
    ok = true;
    printerStatus.push("Ready to Print");
  }
  if (media === "1") printerStatus.push("Paper out");
  if (media === "2") printerStatus.push("Ribbon Out");
  if (media === "4") printerStatus.push("Media Door Open");
  if (media === "8") printerStatus.push("Cutter Fault");
  if (head === "1") printerStatus.push("Printhead Overheating");
  if (head === "2") printerStatus.push("Motor Overheating");
  if (head === "4") printerStatus.push("Printhead Fault");
  if (head === "8") printerStatus.push("Incorrect Printhead");
  if (pause === "1") printerStatus.push("Printer Paused");
  if (!ok && printerStatus.length === 0) printerStatus.push("Error: Unknown Error");
  return printerStatus.join();
}

export async function setupPrinter(): Promise<ZebraPrinterDevice> {
  const bp = getBrowserPrint();
  if (!bp) {
    throw new Error(ZEBRA_BROWSER_PRINT_UNAVAILABLE_MESSAGE);
  }

  return new Promise((resolve, reject) => {
    bp.getDefaultDevice(
      "printer",
      (defaultDevice) => {
        const fallback: ZebraPrinterDevice | null =
          defaultDevice !== null && defaultDevice.connection !== undefined ? defaultDevice : null;

        bp.getLocalDevices(
          (printers) => {
            const usbConnected = printers.filter((p) => p.connection === "usb");
            const chosen = usbConnected[0] ?? fallback;
            if (!chosen) {
              reject(new Error("No Zebra printers could be found."));
              return;
            }
            resolve(chosen);
          },
          (err) => {
            reject(new Error(err ? String(err) : "Failed to list local printers."));
          },
          "printer"
        );
      },
      () => {
        reject(new Error(ZEBRA_BROWSER_PRINT_UNAVAILABLE_MESSAGE));
      }
    );
  });
}

// Zebra BrowserPrint fails on large single payloads (~64KB); each shipping label
// is ~10KB (logo + QR bitmaps), so batched label sends are capped at 4 labels
// (~40KB) per device.send.
const MAX_LABELS_PER_SEND = 4;

function sendChunk(device: ZebraPrinterDevice, data: string): Promise<void> {
  return new Promise((resolve, reject) => {
    device.send(
      data,
      () => resolve(),
      (err) =>
        reject(
          new Error(`An error occurred while printing. Please try again.${err ? String(err) : ""}`)
        )
    );
  });
}

export async function sendPrintData(
  device: ZebraPrinterDevice,
  data: string | string[]
): Promise<string> {
  const summary = await new Promise<string>((resolve, reject) => {
    device.sendThenRead(
      "~HQES",
      (response) => {
        resolve(buildPrinterStatusSummary(response));
      },
      (err) => {
        reject(new Error(err ? String(err) : "Printer status check failed."));
      }
    );
  });

  if (summary !== "Ready to Print") {
    throw new Error(summary || "An error occurred while printing. Please try again.");
  }

  const payloads: string[] = [];
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i += MAX_LABELS_PER_SEND) {
      payloads.push(data.slice(i, i + MAX_LABELS_PER_SEND).join("\n"));
    }
  } else {
    payloads.push(data);
  }
  for (const payload of payloads) {
    await sendChunk(device, payload);
  }
  return "Printing complete";
}
